const COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SKIPPED_FIELD_NAMES = ['form_type', 'utf8', 'contact[id]'];

class CommissionFormComponent extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;
  /** @type {HTMLElement[]} */
  pages = [];
  currentIndex = 0;
  /** @type {number | undefined} */
  #saveTimeout;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!(this.form instanceof HTMLFormElement)) return;

    this.cookieName = 'commission-form-' + (this.dataset.sectionId || '');

    if (this.querySelector('[data-commission-confirmation]')) {
      this.#clearState();
      return;
    }

    this.pagesContainer = this.querySelector('[data-commission-pages]');
    this.pages = Array.from(this.querySelectorAll('[data-commission-page]'));
    this.backBtn = this.querySelector('[data-commission-back]');
    this.nextBtn = this.querySelector('[data-commission-next]');
    this.submitWrap = this.querySelector('[data-commission-submit]');
    this.progressEl = this.querySelector('[data-commission-progress]');
    this.progressBar = this.querySelector('[data-commission-progress-bar]');
    this.progressText = this.querySelector('[data-commission-progress-text]');

    if (!this.pages.length || !this.pagesContainer || !this.backBtn || !this.nextBtn || !this.submitWrap) return;

    const state = this.#loadState();

    this.#applySimpleFields(state);
    this.#setupChoiceGroups(state);

    this.pagesContainer.classList.add('js-paged');

    let startIndex = 0;
    if (state && typeof state.__step === 'number') {
      startIndex = Math.min(Math.max(state.__step, 0), this.pages.length - 1);
    }
    if (this.querySelector('.commission-form__error')) {
      startIndex = 0;
    }

    this.currentIndex = startIndex;
    this.#render();

    this.backBtn.addEventListener('click', () => this.#goBack());
    this.nextBtn.addEventListener('click', () => this.#goNext());
    this.form.addEventListener('input', () => this.#scheduleSave());
    this.form.addEventListener('change', () => this.#scheduleSave());
  }

  /** @param {Record<string, string> | null} state */
  #applySimpleFields(state) {
    if (!state) return;

    for (const [key, value] of Object.entries(state)) {
      if (key === '__step' || typeof value !== 'string') continue;

      const fields = Array.from(document.getElementsByName(key)).filter(
        (field) => this.form instanceof HTMLFormElement && this.form.contains(field)
      );
      if (!fields.length) continue;

      const [field] = fields;
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLTextAreaElement)) continue;

      if (field instanceof HTMLInputElement && field.type === 'radio') {
        for (const radio of fields) {
          if (radio instanceof HTMLInputElement) radio.checked = radio.value === value;
        }
      } else if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'hidden')) {
        continue;
      } else {
        field.value = value;
      }
    }
  }

  /**
   * Mirrors checked values for multi-select choice groups into a single hidden
   * `contact[Label]` input so the notification email gets a clean comma-separated
   * string instead of a raw array. Also wires the "at least one required" toggle,
   * which relies on native validity rather than custom messaging.
   *
   * @param {Record<string, string> | null} state
   */
  #setupChoiceGroups(state) {
    const groups = this.querySelectorAll('[data-commission-choice][data-multiple="true"]');

    groups.forEach((group) => {
      const fieldName = group instanceof HTMLElement ? group.dataset.fieldName : undefined;
      if (!fieldName) return;

      const checkboxes = Array.from(group.querySelectorAll('.commission-choice__input')).filter(
        (input) => input instanceof HTMLInputElement
      );

      const savedValue = state ? state[fieldName] : undefined;
      if (typeof savedValue === 'string') {
        const selected = savedValue.split(', ').filter(Boolean);
        for (const checkbox of checkboxes) {
          if (checkbox instanceof HTMLInputElement) checkbox.checked = selected.includes(checkbox.value);
        }
      }

      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = fieldName;
      group.append(hidden);

      for (const checkbox of checkboxes) {
        checkbox.removeAttribute('name');
      }

      const isRequired = group instanceof HTMLElement && group.dataset.required === 'true';

      const sync = () => {
        const values = checkboxes
          .filter((cb) => cb instanceof HTMLInputElement && cb.checked)
          .map((cb) => (cb instanceof HTMLInputElement ? cb.value : ''));
        hidden.value = values.join(', ');

        if (isRequired) {
          checkboxes.forEach((cb, index) => {
            if (cb instanceof HTMLInputElement) cb.required = values.length === 0 && index === 0;
          });
        }
      };

      group.addEventListener('change', sync);
      sync();
    });
  }

  #render() {
    this.pages.forEach((page, index) => {
      page.classList.toggle('is-active', index === this.currentIndex);
    });

    if (this.backBtn instanceof HTMLElement) this.backBtn.hidden = this.currentIndex === 0;

    const isLast = this.currentIndex === this.pages.length - 1;
    if (this.nextBtn instanceof HTMLElement) this.nextBtn.hidden = isLast;
    if (this.submitWrap instanceof HTMLElement) this.submitWrap.hidden = !isLast;

    const percent = Math.round(((this.currentIndex + 1) / this.pages.length) * 100);
    if (this.progressBar instanceof HTMLElement) {
      this.progressBar.style.setProperty('--commission-form-progress', percent + '%');
    }
    if (this.progressText instanceof HTMLElement) {
      const template = this.progressText.dataset.stepTemplate || '';
      this.progressText.textContent = template
        .replace('__CURRENT__', String(this.currentIndex + 1))
        .replace('__TOTAL__', String(this.pages.length));
    }
  }

  #goNext() {
    const page = this.pages[this.currentIndex];
    if (!page) return;

    const invalidField = page.querySelector(':invalid');
    if (invalidField instanceof HTMLElement && 'reportValidity' in invalidField) {
      /** @type {any} */ (invalidField).reportValidity();
      return;
    }

    if (this.currentIndex < this.pages.length - 1) {
      this.currentIndex++;
      this.#render();
      this.#focusActivePage();
      this.#saveState();
    }
  }

  #goBack() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.#render();
      this.#focusActivePage();
      this.#saveState();
    }
  }

  #focusActivePage() {
    const page = this.pages[this.currentIndex];
    if (!page) return;

    const scrollTarget = this.progressEl instanceof HTMLElement ? this.progressEl : page;
    scrollTarget.scrollIntoView({ behavior: 'instant', block: 'start' });
    this.#suppressHeaderReveal();

    const field = page.querySelector('input, textarea, select');
    if (field instanceof HTMLElement) field.focus({ preventScroll: true });
  }

  /**
   * Any upward scroll delta — even a single-frame instant jump — makes the
   * theme's sticky header (assets/header.js, sticky="scroll-up") reveal itself
   * via `data-sticky-state="active"`. That's a real feature for a user's own
   * scroll gesture, but not for our own step-navigation scroll. Once our scroll
   * has landed and header.js's own scroll-triggered update has run, flip its
   * public dataset back to idle. header.js only re-derives this from a later
   * *real* scroll event, so this correction sticks until the user scrolls again.
   */
  #suppressHeaderReveal() {
    const header = document.getElementById('header-component');
    if (!(header instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (header.dataset.stickyState === 'active') {
          header.dataset.stickyState = 'idle';
          header.dataset.scrollDirection = 'none';
        }
      });
    });
  }

  #scheduleSave() {
    clearTimeout(this.#saveTimeout);
    this.#saveTimeout = window.setTimeout(() => this.#saveState(), 300);
  }

  #saveState() {
    if (!(this.form instanceof HTMLFormElement)) return;

    /** @type {Record<string, string | number>} */
    const data = { __step: this.currentIndex };
    const formData = new FormData(this.form);

    for (const [key, value] of formData.entries()) {
      if (SKIPPED_FIELD_NAMES.includes(key) || typeof value !== 'string' || value === '') continue;
      data[key] = value;
    }

    document.cookie = `${this.cookieName}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
  }

  /** @returns {Record<string, string | number> | null} */
  #loadState() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${this.cookieName}=([^;]*)`));
    if (!match) return null;

    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch {
      return null;
    }
  }

  #clearState() {
    document.cookie = `${this.cookieName}=; path=/; max-age=0`;
  }
}

if (!customElements.get('commission-form-component')) {
  customElements.define('commission-form-component', CommissionFormComponent);
}
